import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../../core/i18n';
import { TenantService } from '../../../../core/tenant/tenant.service';
import { BillingFacade } from '../../store/billing.facade';

const DISMISS_STORAGE_KEY = 'taskeo.trial-banner.dismissed-on';

function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function readStoredDismissal(): string | null {
  try {
    return localStorage.getItem(DISMISS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredDismissal(): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, today());
  } catch {
    /* noop */
  }
}

@Component({
  selector: 'app-trial-banner',
  standalone: true,
  imports: [RouterLink, LucideAngularModule],
  template: `
    @if (shouldShow()) {
      <aside class="trial-banner" [attr.data-color]="color()" role="status">
        <div class="trial-banner__content">
          <lucide-icon name="triangle-alert" [size]="16" aria-hidden="true"></lucide-icon>
          <span>{{ message() }}</span>
        </div>
        <div class="trial-banner__actions">
          <a class="trial-banner__cta" [routerLink]="billingLink()">
            {{ t('billing.trial.bannerCta') }}
          </a>
          <button type="button" class="trial-banner__dismiss" (click)="dismiss()" [attr.aria-label]="t('billing.trial.bannerDismiss')">
            <lucide-icon name="x" [size]="14" aria-hidden="true"></lucide-icon>
          </button>
        </div>
      </aside>
    }
  `,
  styles: [
    `
      :host { display: block; position: sticky; top: 0; z-index: 25; }

      .trial-banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 10px 18px;
        font-size: 13px;
        font-weight: 500;
        border-bottom: 1px solid transparent;
      }

      .trial-banner[data-color='orange'] {
        background: color-mix(in srgb, #f97316 14%, #fff);
        color: #c2410c;
        border-bottom-color: color-mix(in srgb, #f97316 30%, transparent);
      }

      .trial-banner[data-color='red'] {
        background: color-mix(in srgb, #ef4444 14%, #fff);
        color: #b91c1c;
        border-bottom-color: color-mix(in srgb, #ef4444 32%, transparent);
      }

      .trial-banner__content {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        flex: 1;
      }

      .trial-banner__actions { display: inline-flex; gap: 8px; align-items: center; }

      .trial-banner__cta {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 30px;
        padding: 0 12px;
        border-radius: 8px;
        background: #111827;
        color: #fff;
        text-decoration: none;
        font-size: 12px;
        font-weight: 600;
      }

      .trial-banner__cta:hover { background: #0b1220; }

      .trial-banner__dismiss {
        width: 30px;
        height: 30px;
        border-radius: 8px;
        border: 0;
        background: transparent;
        cursor: pointer;
        color: currentColor;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .trial-banner__dismiss:hover {
        background: color-mix(in srgb, currentColor 14%, transparent);
      }

      @media (max-width: 640px) {
        .trial-banner { flex-direction: column; align-items: flex-start; gap: 8px; }
        .trial-banner__actions { align-self: stretch; justify-content: flex-end; }
      }
    `,
  ],
})
export class TrialBannerComponent {
  private readonly facade = inject(BillingFacade);
  private readonly i18n = inject(I18nService);
  private readonly tenant = inject(TenantService);

  readonly t = this.i18n.t.bind(this.i18n);

  readonly dismissedToday = signal(readStoredDismissal() === today());

  readonly color = computed(() => {
    const days = this.facade.trialDaysLeft();
    return days != null && days <= 1 ? 'red' : 'orange';
  });

  readonly message = computed(() => {
    const days = this.facade.trialDaysLeft() ?? 0;
    if (days <= 1) return this.i18n.t('billing.trial.bannerMessageSingle', { days });
    return this.i18n.t('billing.trial.bannerMessagePlural', { days });
  });

  readonly shouldShow = computed(() => {
    if (this.dismissedToday()) return false;
    return this.facade.shouldShowUrgencyBanner();
  });

  readonly billingLink = computed(() => {
    const key = this.tenant.companyKey();
    if (!key) return ['/'];
    return ['/c', key, 'settings', 'billing'];
  });

  dismiss(): void {
    writeStoredDismissal();
    this.dismissedToday.set(true);
  }
}
